import React from 'react';

/**
 * Stops one broken component taking the whole page down.
 *
 * This exists because of a real failure: a decorative streaming-service badge
 * dereferenced a null, and because nothing caught it, React unmounted the
 * entire tree and the app rendered a white page. The cost of the bug should
 * have been one missing icon.
 *
 * Has to be a class - getDerivedStateFromError and componentDidCatch have no
 * hook equivalent.
 *
 * Placement matters more than this component does. Wrapping a tab's contents
 * rather than the whole app means the navigation survives, so someone can move
 * to a tab that still works instead of being stuck on a blank screen. Each tab
 * is rendered conditionally, so switching away unmounts the boundary and
 * clears its error; there is a retry button for everything else.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // The visible message is deliberately short, so put the detail somewhere a
    // report can be pulled from.
    console.error(`💥 ${this.props.label || 'Something'} crashed:`, error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    // Decoration has nothing useful to say when it fails. Its absence is a
    // better outcome than an error panel sitting behind the whole interface.
    if (this.props.silent) return null;

    const label = this.props.label || 'This section';

    return (
      <div className="bg-gray-800 border-2 border-red-600 rounded-lg p-6 my-4 text-center">
        <h3 className="text-xl font-bold text-red-300 mb-2">
          💀 {label} stopped working
        </h3>
        <p className="text-gray-300 mb-1">
          The rest of the app is fine - try another tab, or have another go.
        </p>
        {/* Shown on purpose: these are 20 people who can paste it back. */}
        <p className="text-gray-500 text-xs mb-4 font-mono break-all">
          {String(error.message || error)}
        </p>
        <button
          onClick={() => this.setState({ error: null })}
          className="bg-red-700 hover:bg-red-600 text-white px-4 py-2 rounded font-bold transition-colors"
        >
          Try again
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
